import clsx from 'clsx';
import {useEffect, useState} from 'react';

import {useBlobApi} from '../../contexts/BlobApiContext';
import {Spinner} from '../Spinner/Spinner';

type Props = {
  /** The path to the image (e.g., 'path/to/image.jpg') */
  path: string;
  alt: string;
  className?: string;
  title?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  loadingPlaceholder?: React.JSX.Element;
};

/**
 * Fetches an image through the BlobApi (Ky) and renders it via a blob URL.
 * Use this when a service worker is not available to inject auth headers —
 * e.g. report-client, which authenticates via a URL token rather than MSAL.
 * For SW-enabled apps (studio, connect) use ProtectedImage instead.
 */
export function ProtectedImageFetch({
  path,
  alt,
  className,
  title,
  onLoad,
  onError,
  loadingPlaceholder,
}: Props): React.JSX.Element | null {
  const blobApi = useBlobApi();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const blob = await blobApi.get(`data/${path}`).blob();

        if (!isMounted) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoading(false);
        onLoad?.();
      } catch (err) {
        if (!isMounted) return;

        const error =
          err instanceof Error ? err : new Error('Failed to load image');
        setError(error);
        setIsLoading(false);
        onError?.(error);
      }
    };

    void fetchImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [blobApi, path, onLoad, onError]);

  if (isLoading) return loadingPlaceholder ?? <Spinner />;
  if (error || !blobUrl) return null;

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={clsx(className, 'text-transparent!')}
      title={title}
    />
  );
}

ProtectedImageFetch.displayName = 'ProtectedImageFetch';
